<template>
  <section class="relative rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-slate-800">
    <header class="mb-4 flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-gray-900 dark:text-white">Jack Activity Log</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400">Latest 200 events across the selected scope.</p>
      </div>
      <slot name="actions"></slot>
    </header>

    <div v-if="loading" class="space-y-3">
      <div v-for="index in 5" :key="index" class="flex items-start gap-3">
        <div class="mt-1 h-3 w-3 rounded-full bg-blue-200"></div>
        <div class="flex-1 animate-pulse space-y-2">
          <div class="h-4 w-40 rounded bg-gray-100 dark:bg-slate-700"></div>
          <div class="h-3 w-full rounded bg-gray-100 dark:bg-slate-700"></div>
        </div>
      </div>
    </div>

    <div v-else-if="!processedLogs.length" class="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500 shadow-inner dark:border-gray-600 dark:bg-slate-900 dark:text-gray-400">
      No Jack activity captured yet. Trigger an action to see it appear instantly.
    </div>

    <ol v-else class="relative space-y-6 border-l border-gray-200 pl-6 dark:border-gray-700">
      <li
        v-for="log in processedLogs"
        :key="log.id"
        class="group relative"
      >
        <span
          class="absolute -left-[10px] mt-1 h-3 w-3 rounded-full border-2 border-white bg-blue-500 shadow group-hover:scale-125 dark:border-slate-800"
          :class="{
            'bg-amber-500': log.isElevated,
            'bg-emerald-500': !log.isElevated,
            'ring-4 ring-blue-300/60': isHighlighted(log),
          }"
        ></span>
        <div
          class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition group-hover:border-blue-200 group-hover:shadow-md dark:border-gray-700 dark:bg-slate-800"
          :class="{ 'border-blue-400 shadow-lg shadow-blue-200/40 dark:border-blue-500/80': isHighlighted(log) }"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <span :class="methodBadgeClass(log.method)">{{ log.method }}</span>
                <span class="font-mono text-xs text-gray-600 dark:text-gray-300">{{ log.route }}</span>
              </div>
              <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>Access #{{ log.jack_access_id }}</span>
                <span>Store #{{ log.store_id }}</span>
                <span>User #{{ log.user_id }}</span>
                <span v-if="log.status_code" :class="statusBadgeClass(log.status_code)">Status {{ log.status_code }}</span>
              </div>
            </div>
            <div class="text-right text-xs text-gray-500 dark:text-gray-400">
              <div>{{ log.absolute }}</div>
              <div>{{ log.relative }}</div>
            </div>
          </div>

          <div v-if="log.permissions_used?.length" class="mt-3 flex flex-wrap gap-2">
            <span
              v-for="permission in log.permissions_used"
              :key="permission"
              class="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-blue-600 dark:bg-blue-900/40 dark:text-blue-200"
            >
              <vue-feather :type="isElevated(permission) ? 'unlock' : 'eye'" class="h-3.5 w-3.5" />
              {{ permission }}
            </span>
          </div>

          <div v-if="log.request_body_hash" class="mt-3 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600 dark:bg-slate-900 dark:text-gray-300">
            Request hash: <span class="font-mono">{{ log.request_body_hash }}</span>
          </div>
        </div>
      </li>
    </ol>
  </section>
</template>

<script>
import { format, formatDistanceToNowStrict } from "date-fns";

const ELEVATED_PATTERN = /(WRITE|MANAGE|APPROVE|PROCESS|CHANGE)/i;

export default {
  name: "JackLogTimeline",
  props: {
    logs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    highlightAccessId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    processedLogs() {
      return (this.logs || []).map((log) => {
        const createdAt = log.created_at ? new Date(log.created_at) : null;
        return {
          ...log,
          absolute: createdAt ? format(createdAt, "MMM dd, yyyy HH:mm") : "",
          relative: createdAt ? `${formatDistanceToNowStrict(createdAt)} ago` : "",
          isElevated: Array.isArray(log.permissions_used)
            ? log.permissions_used.some((permission) => ELEVATED_PATTERN.test(permission))
            : ELEVATED_PATTERN.test(log.request_body_hash || ""),
        };
      });
    },
  },
  methods: {
    isElevated(permission) {
      return ELEVATED_PATTERN.test(permission || "");
    },
    isHighlighted(log) {
      if (!this.highlightAccessId) {
        return false;
      }
      return String(log.jack_access_id) === String(this.highlightAccessId);
    },
    methodBadgeClass(method) {
      const normalized = String(method || "").toUpperCase();
      const palette = {
        GET: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-200",
        POST: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200",
        PUT: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-200",
        PATCH: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-200",
        DELETE: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-200",
      };
      return [
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide",
        palette[normalized] || "bg-gray-100 text-gray-600 dark:bg-slate-900 dark:text-gray-300",
      ];
    },
    statusBadgeClass(status) {
      const code = Number(status);
      if (!Number.isFinite(code)) {
        return "inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600 dark:bg-slate-900 dark:text-gray-300";
      }
      if (code >= 500) {
        return "inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-semibold text-red-600 dark:bg-red-900/50 dark:text-red-200";
      }
      if (code >= 400) {
        return "inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-600 dark:bg-amber-900/50 dark:text-amber-200";
      }
      return "inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-200";
    },
  },
};
</script>

